import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import './SharePropertyModal.css';

const SharePropertyModal = ({
  isOpen,
  onClose,
  property,
  getPropertyAddress,
  getPropertyPrice,
  getPropertyDetails,
  getPropertyPhoto,
  getPropertyMls,
}) => {
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyerIds, setSelectedBuyerIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingBuyers, setIsLoadingBuyers] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { success: number, failed: number, total: number }

  const getFirebaseToken = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    return await currentUser.getIdToken();
  }, []);

  // Fetch all buyers from API
  const fetchBuyers = useCallback(async () => {
    setIsLoadingBuyers(true);
    try {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) return;

      const response = await apiClient.get(API_ENDPOINTS.userContacts, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        params: { role: 'BUYER' },
        timeout: 15000,
      });

      const data = response.data;
      let buyersList = [];
      if (Array.isArray(data)) buyersList = data;
      else if (data?.data && Array.isArray(data.data)) buyersList = data.data;
      else if (data?.content && Array.isArray(data.content)) buyersList = data.content;
      else if (data?.users && Array.isArray(data.users)) buyersList = data.users;

      // Batch-fetch profile photos
      const buyersWithPhotos = await Promise.all(
        buyersList.map(async (buyer) => {
          let photoUrl = buyer.profilePhotoBase64 || buyer.profilePhotoUrl || null;
          if (!photoUrl && buyer.id) {
            try {
              const photoResponse = await apiClient.get(
                API_ENDPOINTS.userPhoto(buyer.id),
                {
                  headers: { 'Authorization': `Bearer ${firebaseToken}` },
                  responseType: 'blob',
                  timeout: 5000,
                }
              );
              if (photoResponse.data && photoResponse.data.size > 0) {
                photoUrl = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(photoResponse.data);
                });
              }
            } catch {
              // No photo available
            }
          }
          const name = [buyer.firstName, buyer.lastName].filter(Boolean).join(' ') || buyer.name || buyer.email || 'Unknown';
          return {
            ...buyer,
            displayName: name,
            photoUrl,
            initials: name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?',
          };
        })
      );

      setBuyers(buyersWithPhotos);
    } catch (err) {
      console.error('[ShareProperty] Error fetching buyers:', err);
      setBuyers([]);
    } finally {
      setIsLoadingBuyers(false);
    }
  }, [getFirebaseToken]);

  // Fetch buyers when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBuyers();
      setSelectedBuyerIds(new Set());
      setSearchQuery('');
      setSendResult(null);
    }
  }, [isOpen, fetchBuyers]);

  // Build chatId map from Firestore: { participantUid: chatId }
  const buildChatIdMap = useCallback(async () => {
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return {};

    try {
      const userChatsRef = collection(db, 'user_chats', currentUid, 'chats');
      const snapshot = await getDocs(userChatsRef);
      const chatIdMap = {};

      for (const chatRefDoc of snapshot.docs) {
        const chatRefData = chatRefDoc.data();
        const chatId = chatRefData.chatId || chatRefDoc.id;

        const chatDocRef = doc(db, 'chats', chatId);
        const chatDoc = await getDoc(chatDocRef);

        if (chatDoc.exists()) {
          const chatData = chatDoc.data();
          const participants = chatData.participants || [];
          // Map each participant (other than current user) to this chatId
          participants.forEach(uid => {
            if (uid !== currentUid) {
              chatIdMap[uid] = chatId;
            }
          });
        }
      }

      return chatIdMap;
    } catch (err) {
      console.error('[ShareProperty] Error building chatId map:', err);
      return {};
    }
  }, []);

  // Build { buyerFirebaseUid: transactionId } from the sharer's own active PURCHASE
  // transactions — mirrors mobile SharePropertyScreen.tsx's pre-fetch step (fetch own
  // active transactions once, then map each BUYER participant to that transaction),
  // rather than a per-recipient lookup call inside the send loop.
  const buildBuyerTxnMap = useCallback(async (firebaseToken) => {
    const buyerTxnMap = {};
    try {
      const txnResponse = await apiClient.get(
        `${API_ENDPOINTS.transactions}?status=PENDING&status=ACTIVE&type=PURCHASE`,
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      const txnData = txnResponse.data;
      const txnList = Array.isArray(txnData) ? txnData : (txnData?.data || txnData?.content || txnData?.transactions || []);

      await Promise.all(txnList.map(async (txn) => {
        const txnId = txn.id || txn.transactionId;
        if (!txnId) return;
        try {
          const participantsResponse = await apiClient.get(
            API_ENDPOINTS.transactionParticipants(txnId),
            { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
          );
          const pData = participantsResponse.data;
          const pList = Array.isArray(pData) ? pData : (pData?.data || pData?.participants || pData?.content || []);
          pList.forEach(participant => {
            const role = (participant.role || participant.participantRole || participant.type || '').toUpperCase();
            const uid = participant.firebaseUid || participant.uid;
            if (role === 'BUYER' && uid) buyerTxnMap[uid] = txnId;
          });
        } catch (err) {
          console.error('[ShareProperty] Error fetching participants for transaction:', txnId, err);
        }
      }));
    } catch (err) {
      console.error('[ShareProperty] Error fetching transactions for auto-add:', err);
    }
    return buyerTxnMap;
  }, []);

  // After a successful share, mark the property "interested" on the recipient's
  // matching active transaction — mirrors mobile SharePropertyScreen.tsx's auto-add
  // side effect (checkPropertyExists → addInterestedProperty). Best-effort: failures
  // here don't affect the share's success/fail count, since the message already sent.
  const autoAddInterestedProperty = async (transactionId, firebaseToken) => {
    try {
      const mlsId = getPropertyMls(property);
      if (!mlsId) return;

      const checkResponse = await apiClient.get(
        API_ENDPOINTS.checkInterestedProperty(transactionId, String(mlsId)),
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      if (checkResponse.data?.exists) return;

      await apiClient.post(
        API_ENDPOINTS.interestedProperties(transactionId),
        {
          mlsId: String(mlsId),
          propertyAddress: getPropertyAddress(property),
          propertyCity: property.city || property.propertyCity || '',
          propertyState: property.state || property.propertyState || '',
          propertyZip: property.zipCode || property.zip || property.postalCode || property.propertyZip || '',
          listPrice: property.listPrice || property.price || property.askingPrice || property.salePrice || 0,
          bedrooms: property.bedrooms || property.beds || property.bedroomCount || undefined,
          bathrooms: property.bathrooms || property.baths || property.bathroomCount || undefined,
          squareFeet: property.squareFeet || property.sqft || property.livingArea || property.area || undefined,
          primaryPhoto: getPropertyPhoto(property) || undefined,
        },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
    } catch (err) {
      console.error('[ShareProperty] Error auto-adding interested property:', err);
    }
  };

  // Handle share
  const handleShare = async () => {
    if (selectedBuyerIds.size === 0 || !property) return;

    setIsSending(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) return;

      // Build chatId map from Firestore, and the buyer→transaction map for the
      // auto-add-interested side effect, in parallel.
      const [chatIdMap, buyerTxnMap] = await Promise.all([
        buildChatIdMap(),
        buildBuyerTxnMap(firebaseToken),
      ]);

      const selectedBuyers = buyers.filter(b =>
        selectedBuyerIds.has(b.id || b.odooId || b.firebaseUid)
      );

      // Build property data payload (matching mobile app format)
      const propAddress = getPropertyAddress(property);
      const propPrice = getPropertyPrice(property);
      const propertyPayload = {
        id: property.id || property.odooId || '',
        mlsId: getPropertyMls(property),
        price: propPrice,
        address: propAddress,
        city: property.city || property.propertyCity || '',
        state: property.state || property.propertyState || '',
        zipCode: property.zipCode || property.zip || property.postalCode || property.propertyZip || '',
        beds: property.bedrooms || property.beds || property.bedroomCount || '',
        baths: property.bathrooms || property.baths || property.bathroomCount || '',
        sqft: property.squareFeet || property.sqft || property.livingArea || property.area || '',
        primaryPhoto: getPropertyPhoto(property),
        status: property.status || property.listingStatus || property.propertyStatus || '',
      };

      const displayText = `Check out this property: ${propAddress} - ${propPrice}`;

      for (const buyer of selectedBuyers) {
        try {
          const buyerUid = buyer.firebaseUid || buyer.uid;
          const chatId = chatIdMap[buyerUid];

          if (!chatId) {
            console.warn('[ShareProperty] No chat found for buyer:', buyer.displayName, buyerUid);
            failedCount++;
            continue;
          }

          await apiClient.post(
            API_ENDPOINTS.chatMessages(chatId),
            {
              content: displayText,
              messageType: 'property',
              metadata: {
                propertyData: propertyPayload,
              },
            },
            {
              headers: { 'Authorization': `Bearer ${firebaseToken}` },
              timeout: 10000,
            }
          );
          successCount++;

          const buyerTxnId = buyerTxnMap[buyerUid];
          if (buyerTxnId) autoAddInterestedProperty(buyerTxnId, firebaseToken);
        } catch (err) {
          console.error('[ShareProperty] Error sharing with buyer:', buyer.displayName, err);
          failedCount++;
        }
      }

      setSendResult({
        success: successCount,
        failed: failedCount,
        total: selectedBuyers.length,
      });
    } catch (err) {
      console.error('[ShareProperty] Error in share process:', err);
      setSendResult({
        success: successCount,
        failed: failedCount || selectedBuyerIds.size,
        total: selectedBuyerIds.size,
      });
    } finally {
      setIsSending(false);
    }
  };

  const toggleBuyer = (buyerId) => {
    setSelectedBuyerIds(prev => {
      const next = new Set(prev);
      if (next.has(buyerId)) next.delete(buyerId);
      else next.add(buyerId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBuyerIds.size === filteredBuyers.length) {
      setSelectedBuyerIds(new Set());
    } else {
      setSelectedBuyerIds(new Set(filteredBuyers.map(b => b.id || b.odooId || b.firebaseUid)));
    }
  };

  const getBuyerId = (buyer) => buyer.id || buyer.odooId || buyer.firebaseUid;

  const getAvatarColor = (name) => {
    const colors = ['#6c5ce7', '#00b894', '#fd79a8', '#fdcb6e', '#74b9ff', '#a29bfe', '#ff7675', '#55efc4'];
    if (!name) return colors[0];
    const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  };

  // Filter buyers by search
  const filteredBuyers = buyers.filter(buyer => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (buyer.displayName && buyer.displayName.toLowerCase().includes(q)) ||
      (buyer.email && buyer.email.toLowerCase().includes(q)) ||
      (buyer.phone && buyer.phone.includes(q)) ||
      (buyer.phoneNumber && buyer.phoneNumber.includes(q))
    );
  });

  if (!isOpen) return null;

  const allSelected = filteredBuyers.length > 0 && selectedBuyerIds.size === filteredBuyers.length;

  return (
    <div className="spm-overlay" onClick={onClose}>
      <div className="spm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Result Screen */}
        {sendResult ? (
          <div className="spm-result">
            <div className="spm-result-icon">
              {sendResult.failed === 0 ? (
                <div className="spm-result-icon-circle spm-result-success">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              ) : (
                <div className="spm-result-icon-circle spm-result-partial">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 9v4M12 17h.01" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
            </div>
            <h3 className="spm-result-title">
              {sendResult.failed === 0 ? 'Property Shared!' : 'Sharing Complete'}
            </h3>
            <p className="spm-result-desc">
              {sendResult.success > 0 && (
                <span>Successfully shared with {sendResult.success} buyer{sendResult.success > 1 ? 's' : ''}.</span>
              )}
              {sendResult.failed > 0 && (
                <span> Failed to share with {sendResult.failed} buyer{sendResult.failed > 1 ? 's' : ''} (no chat found).</span>
              )}
            </p>
            <button className="spm-result-btn" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="spm-header">
              <h2 className="spm-title">Share Property</h2>
              <div className="spm-header-right">
                <button
                  className={`spm-select-all-btn ${allSelected ? 'active' : ''}`}
                  onClick={toggleSelectAll}
                  disabled={isLoadingBuyers || filteredBuyers.length === 0}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
                <button className="spm-close-btn" onClick={onClose}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Property Card */}
            {property && (
              <div className="spm-property-card">
                <div className="spm-property-image">
                  {getPropertyPhoto(property) ? (
                    <img src={getPropertyPhoto(property)} alt="Property" />
                  ) : (
                    <div className="spm-property-placeholder">&#127968;</div>
                  )}
                </div>
                <div className="spm-property-info">
                  <div className="spm-property-price">{getPropertyPrice(property)}</div>
                  <div className="spm-property-address">{getPropertyAddress(property)}</div>
                  <div className="spm-property-details">{getPropertyDetails(property)}</div>
                </div>
              </div>
            )}

            {/* Search */}
            <div className="spm-search-wrapper">
              <svg className="spm-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                className="spm-search-input"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Counter */}
            <div className="spm-counter">
              {selectedBuyerIds.size} buyer{selectedBuyerIds.size !== 1 ? 's' : ''} selected
            </div>

            {/* Buyer List */}
            <div className="spm-buyer-list">
              {isLoadingBuyers ? (
                <div className="spm-loading">
                  <div className="spm-loading-spinner"></div>
                  <span>Loading buyers...</span>
                </div>
              ) : filteredBuyers.length === 0 ? (
                <div className="spm-empty">
                  <p>{searchQuery ? 'No buyers match your search' : 'No buyers found'}</p>
                </div>
              ) : (
                filteredBuyers.map((buyer) => {
                  const buyerId = getBuyerId(buyer);
                  const isSelected = selectedBuyerIds.has(buyerId);
                  return (
                    <div
                      key={buyerId}
                      className={`spm-buyer-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleBuyer(buyerId)}
                    >
                      <div className="spm-buyer-avatar">
                        {buyer.photoUrl ? (
                          <img src={buyer.photoUrl} alt={buyer.displayName} className="spm-buyer-avatar-img" />
                        ) : (
                          <div
                            className="spm-buyer-avatar-initials"
                            style={{ backgroundColor: getAvatarColor(buyer.displayName) }}
                          >
                            {buyer.initials}
                          </div>
                        )}
                      </div>
                      <div className="spm-buyer-info">
                        <div className="spm-buyer-name">{buyer.displayName}</div>
                        <div className="spm-buyer-contact">
                          {buyer.email || ''}
                          {buyer.email && (buyer.phone || buyer.phoneNumber) ? ' \u2022 ' : ''}
                          {buyer.phone || buyer.phoneNumber || ''}
                        </div>
                      </div>
                      <div className={`spm-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="spm-footer">
              <button
                className="spm-share-btn"
                onClick={handleShare}
                disabled={selectedBuyerIds.size === 0 || isSending}
              >
                {isSending ? 'Sharing...' : `Share with Buyer${selectedBuyerIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharePropertyModal;
